import { forwardRef, useEffect, useState, type Ref } from 'react';
import { CartChangeType, useCartEvents } from '@hungryroot/cart';
import {
  CustomizationStates,
  type CustomizationStateReady,
  type CustomizationStateShuffling,
  type CustomizationStateSkipped,
} from '../../../providers/CustomizationProvider/CustomizationProvider';
import {
  CartButtonReactive,
  Variant as CartButtonReactiveVariant,
  type Props as CartButtonReactiveProps,
} from '../CartStatusButton/CartStatusButton';

type ReadyProps = {
  state: CustomizationStateReady['state'];
  saveThreshold: CustomizationStateReady['saveThreshold'];
  hasChanges: CustomizationStateReady['hasChanges'];
  cart: Pick<CustomizationStateReady['cart'], 'points' | 'plan_points'>;
  order: Pick<
    CustomizationStateReady['order'],
    'cutoff_datetime' | 'preferred_date'
  >;
};

type ShufflingProps = {
  state: CustomizationStateShuffling['state'];
  order: Pick<
    CustomizationStateReady['order'],
    'cutoff_datetime' | 'preferred_date'
  >;
};

type SkippedProps = {
  state: CustomizationStateSkipped['state'];
  saveThreshold: CustomizationStateSkipped['saveThreshold'];
  hasChanges: CustomizationStateSkipped['hasChanges'];
  cart: Pick<CustomizationStateSkipped['cart'], 'points' | 'plan_points'>;
  order: Pick<
    CustomizationStateSkipped['order'],
    'cutoff_datetime' | 'preferred_date'
  >;
};

type Props = {
  onClick?: CartButtonReactiveProps['onClick'];
  order: CartButtonReactiveProps['order'];
  customization: ReadyProps | SkippedProps | ShufflingProps;
  testid?: string;
  cartButtonAnimationClassName?: string;
};

/**
 * On desktop, see `CartSummary` for the cart button.
 */
export const CartStatusButtonReactive = forwardRef(
  (props: Props, ref: Ref<HTMLButtonElement>) => {
    const {
      order,
      onClick,
      customization,
      testid,
      cartButtonAnimationClassName,
    } = props;
    const { state: customizationState } = customization;
    const [variantOverride, setVariantOverride] = useState<
      CartButtonReactiveProps['variant'] | null
    >(null);

    const isBelowSaveThreshold =
      'saveThreshold' in customization
        ? customization.saveThreshold.isBelowSaveThreshold
        : false;

    const cartHasChanges =
      'hasChanges' in customization ? customization.hasChanges : false;

    useCartEvents({
      onRequestChange(e) {
        switch (e.type) {
          case CartChangeType.RequestAddProduct:
          case CartChangeType.RequestAddRecipe: {
            setVariantOverride({
              type: CartButtonReactiveVariant.Added,
            });
            return;
          }
          case CartChangeType.RequestRemoveProduct:
          case CartChangeType.RequestRemoveRecipe: {
            setVariantOverride({
              type: CartButtonReactiveVariant.Removed,
            });
            return;
          }
        }
      },
    });

    useEffect(() => {
      if (variantOverride === null) {
        return;
      }

      const timeout = setTimeout(() => {
        setVariantOverride(null);
      }, 3000);

      return () => {
        clearTimeout(timeout);
      };
    }, [variantOverride]);

    function getVariant() {
      if (variantOverride !== null) {
        return variantOverride;
      }

      if (isBelowSaveThreshold === true) {
        return {
          type: CartButtonReactiveVariant.BelowSaveThreshold,
        };
      }

      if (cartHasChanges === true) {
        return {
          type: CartButtonReactiveVariant.UnsavedChanges,
        };
      }

      if (customizationState === CustomizationStates.Skipped) {
        return {
          type: CartButtonReactiveVariant.Skipped,
        };
      }

      return {
        type: CartButtonReactiveVariant.NoChanges,
      };
    }

    const variant = getVariant();

    return (
      <CartButtonReactive
        testid={testid}
        ref={ref}
        variant={variant}
        order={order}
        onClick={onClick}
        cartButtonAnimationClassName={cartButtonAnimationClassName}
      />
    );
  }
);
